import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiClient } from "~/hooks/use-api-client";
import { logger } from "~/lib/logger";
import type { SurveyDeliveryPrompt } from "../types";

interface DeliveryPromptResponse {
    prompt: SurveyDeliveryPrompt | null;
}

export function useSurveyDeliveryPrompt({
    surfaceKey,
    enabled = true,
    context = {},
}: {
    surfaceKey: string;
    enabled?: boolean;
    context?: Record<string, string | number | boolean | null>;
}) {
    const apiClient = useApiClient();
    const isEmbeddedSurface =
        surfaceKey === "session_upload" || surfaceKey === "assessment";

    return useQuery({
        queryKey: ["surveys", "delivery", surfaceKey, context],
        enabled,
        queryFn: async () => {
            const startedAt = performance.now();
            if (isEmbeddedSurface) {
                logger
                    .withMetadata({
                        surfaceKey,
                        context,
                    })
                    .info("Survey delivery prompt fetch started");
            }

            try {
                const response = await apiClient.get("/surveys/delivery", {
                    params: {
                        surfaceKey,
                        context: JSON.stringify(context),
                    },
                });

                if (!response.ok) {
                    throw new Error("Failed to load survey delivery prompt");
                }

                const data = (await response.json()) as DeliveryPromptResponse;
                if (isEmbeddedSurface) {
                    logger
                        .withMetadata({
                            surfaceKey,
                            context,
                            hasPrompt: !!data.prompt,
                            surveyId: data.prompt?.survey.id ?? null,
                            questionId: data.prompt?.question.id ?? null,
                            durationMs: Math.round(
                                performance.now() - startedAt,
                            ),
                        })
                        .info("Survey delivery prompt fetch resolved");
                }
                return data.prompt;
            } catch (error) {
                if (isEmbeddedSurface) {
                    logger
                        .withError(error)
                        .withMetadata({
                            surfaceKey,
                            context,
                            durationMs: Math.round(
                                performance.now() - startedAt,
                            ),
                        })
                        .error("Survey delivery prompt fetch failed");
                }
                throw error;
            }
        },
        staleTime: 1000 * 30,
        refetchOnWindowFocus: false,
    });
}

export function useAnswerSurveyDeliveryPrompt(_surfaceKey: string) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            prompt,
            answer,
        }: {
            prompt: SurveyDeliveryPrompt;
            answer: string | string[];
        }) => {
            const response = await apiClient.post("/surveys/delivery", {
                intent: "answer",
                surveyId: prompt.survey.id,
                deliveryId: prompt.deliveryId,
                responses: {
                    [prompt.question.id]: answer,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to save survey answer");
            }

            return response.json() as Promise<{
                success: boolean;
                isComplete: boolean;
            }>;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["surveys", "delivery"],
                exact: false,
            });
            queryClient.invalidateQueries({
                queryKey: ["surveys", "active"],
                exact: false,
            });
        },
    });
}

export function useDismissSurveyDeliveryPrompt(_surfaceKey: string) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ prompt }: { prompt: SurveyDeliveryPrompt }) => {
            const response = await apiClient.post("/surveys/delivery", {
                intent: "dismiss",
                surveyId: prompt.survey.id,
                deliveryId: prompt.deliveryId,
            });

            if (!response.ok) {
                throw new Error("Failed to dismiss survey prompt");
            }

            return response.json() as Promise<{ success: boolean }>;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["surveys", "delivery"],
                exact: false,
            });
            queryClient.invalidateQueries({
                queryKey: ["surveys", "active"],
                exact: false,
            });
        },
    });
}
